/**
 * The four-eyes queue: the runs waiting for a second pair of eyes.
 *
 * **You cannot approve your own request.** The screen disables it and says why; the server
 * refuses it independently with `403 self_approval_forbidden`, which is the check that actually
 * matters. A control the client merely hides is not a control.
 *
 * **It shows payout runs and tax writes.** The design is one queue for payouts, refunds,
 * write-offs, tax and settings. Write-offs still have no four-eyes endpoint (blocked on E19), and
 * that is named in the wiring note rather than mocked into this table: a queue whose rows are half
 * real and half invented is one an operator would work through believing all of it.
 *
 * **Tax writes are decided here** (backend 29 §4.3): a new rate version, a TDS challan and a GST
 * remittance each park on the Tax screen and run only when a second finance admin approves. The
 * approval re-checks the write against the ledger as it stands, so a refusal (a window that now
 * overlaps, a deduction somebody else deposited) comes back as a toast and the row stays.
 *
 * **Refund corrections DO exist, and they are decided on the Refunds screen**: a goodwill refund
 * above the ceiling parks as an `adjustment` in `pending_approval`. The pointer below carries the
 * server's own count of them, so "nothing is waiting here" is never read as "nothing is waiting".
 *
 * **Settings approvals DO exist, and they render on the Settings screen** (E52-era; CSR-022), not
 * here. That is the right home — the approval is about a setting, and the approver arrives from
 * the screen they were asked about. ⛔ But an approver who opens *this* screen sees payout runs
 * and would reasonably conclude that is the whole queue, so there is a pointer below. One
 * rendering, one home: duplicating the list here would be a second answer to "what is waiting".
 *
 * **Manual Punya adjustments are decided here** (doc 38 §4.7): the Punya tab only parks the
 * request, and nothing moves until a different finance admin approves it from this queue.
 * Unlike tax, the requester cannot withdraw their own; it waits for a colleague either way.
 *
 * **No threshold is printed here.** The ceiling lives in `settlement.four_eyes_threshold_paise`
 * and the server applies it; a copy in this file would be a second answer to "what is the
 * ceiling", and a copy of a server list has been wrong three times already.
 */
import { useState, type ReactNode } from 'react';
import { StatusChip } from '../components/primitives';
import { DataTable, DetailPage } from '../components/data';
import { KV } from '../components/entities';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, Consequences, Money, ScopeAction, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { newIdempotencyKey } from '../lib/api/idempotency';
import { useDecideTaxApproval, useTaxApprovals, type TaxApprovalRow } from '../lib/api/hooks/tax';
import {
  useDecidePunyaAdjustment,
  usePunyaAdjustments,
  type PunyaAdjustmentRow,
} from '../lib/api/hooks/loyalty';
import {
  useDecidePayoutBatch,
  usePayoutBatch,
  usePayoutBatches,
} from '../lib/api/hooks/payouts';
import { useAdjustments } from '../lib/api/hooks/refunds';
import { batchTone, payoutTone } from './Payouts';

const PAGE_SIZE = 50;

/** A link-styled button to the screen where the rest of the four-eyes work lives. */
function Pointer({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--c-saffron-text)', font: 'inherit' }}
    >
      {children}
    </button>
  );
}

export function Approvals() {
  const { scope, adminId, go, toast } = useConsole();
  const [openId, setOpenId] = useState<number | null>(null);
  const [confirm, setConfirm] = useState<{ decision: 'approve' | 'reject'; key: string } | null>(
    null,
  );

  const queue = usePayoutBatches({
    page: 1,
    page_size: PAGE_SIZE,
    batch_status: 'pending_approval',
  });
  const detail = usePayoutBatch(openId);
  const decide = useDecidePayoutBatch();
  const corrections = useAdjustments('pending_approval');

  const fail = (error: unknown) => toast({ tone: 'error', message: errorMessage(error) });
  const isSelf = detail.isSuccess && detail.data.created_by_admin_id === adminId;

  if (openId !== null) {
    return (
      <DetailPage
        title={`Payout run #${openId}`}
        onClose={() => setOpenId(null)}
        actions={
          detail.isSuccess ? (
            <>
              <span
                data-tip={
                  isSelf ? 'You built this run — a different finance admin must decide it' : undefined
                }
                style={{ display: 'inline-flex' }}
              >
                <ScopeAction
                  scope={scope}
                  need="finance"
                  size="sm"
                  variant="secondary"
                  disabled={isSelf}
                  onClick={() => setConfirm({ decision: 'reject', key: newIdempotencyKey() })}
                >
                  Reject
                </ScopeAction>
              </span>
              <span
                data-tip={
                  isSelf ? 'Self-approval is refused by the server (403 self_approval_forbidden)' : undefined
                }
                style={{ display: 'inline-flex' }}
              >
                <ScopeAction
                  scope={scope}
                  need="finance"
                  size="sm"
                  disabled={isSelf}
                  onClick={() => setConfirm({ decision: 'approve', key: newIdempotencyKey() })}
                >
                  Approve
                </ScopeAction>
              </span>
            </>
          ) : null
        }
      >
        {detail.isPending && <LoadingState message="Reading the run…" />}
        {detail.isError && (
          <ErrorState title="Could not read the run" cause={errorMessage(detail.error)} />
        )}

        {detail.isSuccess && (
          <>
            {isSelf && (
              <Consequences
                tone="error"
                items={[
                  'You built this run. The server refuses self-approval — ask another finance admin to decide it.',
                ]}
              />
            )}
            <Section title="The run" note="What you are agreeing to, in full">
              <KV
                rows={[
                  ['Run date', detail.data.batch_on],
                  [
                    'State',
                    <StatusChip
                      key="s"
                      tone={batchTone(detail.data.status)}
                      label={detail.data.status.replace(/_/g, ' ')}
                    />,
                  ],
                  ['Payouts', String(detail.data.payout_count)],
                  ['Total', <Money key="t" paise={detail.data.total_amount_paise} showPaise />],
                  ['Built by', `admin #${detail.data.created_by_admin_id}`],
                ]}
              />
            </Section>

            <Section
              title="Every payout in the run"
              note="Not a summary — the figures you are agreeing to, one provider at a time"
              pad={false}
            >
              <DataTable
                density="compact"
                columns={[
                  { key: 'code', label: 'Payout', mono: true },
                  { key: 'dest', label: 'To' },
                  { key: 'amount', label: 'Amount', align: 'right', mono: true },
                  { key: 'state', label: 'State' },
                ]}
                rows={detail.data.payouts.map((p) => ({
                  id: p.id,
                  code: p.public_code,
                  dest: `••••${p.bank_account_last4}`,
                  amount: <Money paise={p.amount_paise} showPaise />,
                  state: <StatusChip tone={payoutTone(p.status)} label={p.status} />,
                }))}
              />
            </Section>
          </>
        )}

        {confirm !== null && detail.isSuccess && (
          <ConfirmAction
            open
            title={
              confirm.decision === 'approve'
                ? 'Approve this run as the second pair of eyes?'
                : 'Reject this run?'
            }
            confirmLabel={confirm.decision === 'approve' ? 'Approve run' : 'Reject run'}
            phrase={confirm.decision === 'approve' ? 'APPROVE' : 'REJECT'}
            tone="error"
            reason
            consequences={
              confirm.decision === 'approve'
                ? [
                    `You are agreeing to ${detail.data.payout_count} payouts.`,
                    'The run becomes executable. Your name is recorded alongside the person who built it.',
                  ]
                : [
                    'Nothing is sent, and every payable goes back into the queue for a future run.',
                    'The payouts stay as the record that this run was built and refused.',
                  ]
            }
            onCancel={() => setConfirm(null)}
            onConfirm={(why) =>
              decide.mutate(
                { batchId: openId, decision: confirm.decision, reason: why, key: confirm.key },
                {
                  onSuccess: () => {
                    setConfirm(null);
                    setOpenId(null);
                    toast({
                      tone: confirm.decision === 'approve' ? 'success' : 'info',
                      message:
                        confirm.decision === 'approve' ? 'Approved' : 'Rejected — the money is back in the queue',
                    });
                  },
                  onError: (error) => {
                    setConfirm(null);
                    fail(error);
                  },
                },
              )
            }
          />
        )}
      </DetailPage>
    );
  }

  return (
    <>
      {/* ⛔ Not everything four-eyes lands on this screen, and a queue that stays silent about
          that reads as "nothing is waiting" when something is. */}
      <p style={{ margin: 0, fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
        Parked <strong>settings</strong> changes are approved on the Settings screen, beside the
        setting they change. <Pointer onClick={() => go('settings')}>Open Settings</Pointer>.
        Refund <strong>corrections</strong> above the goodwill ceiling
        {corrections.isSuccess && ` (${corrections.data.total} waiting)`} are approved on the
        Refunds screen. <Pointer onClick={() => go('refunds')}>Open Refunds</Pointer>. Write-offs
        have no four-eyes endpoint yet.
      </p>
      {queue.isPending && <LoadingState message="Reading the queue…" />}
      {queue.isError && (
        <ErrorState title="Could not read the queue" cause={errorMessage(queue.error)} />
      )}
      {queue.isSuccess && queue.data.items.length === 0 && (
        <Section title="Waiting for a checker" note="Payout runs above the four-eyes ceiling">
          <EmptyState
            inline
            title="No payout run is waiting for a second pair of eyes"
            body="A payout run lands here when its total is above the four-eyes ceiling. Below it, one pair of hands is the sanctioned cost of routine work."
          />
        </Section>
      )}
      {queue.isSuccess && queue.data.items.length > 0 && (
        <Section
          title="Waiting for a checker"
          note="Somebody else built these. You cannot decide your own."
          pad={false}
        >
          <DataTable
            density="comfortable"
            onRowClick={(row) => setOpenId(Number(row.id))}
            columns={[
              { key: 'on', label: 'Run date' },
              { key: 'count', label: 'Payouts', align: 'right' },
              { key: 'amount', label: 'Total', align: 'right', mono: true },
              { key: 'maker', label: 'Built by' },
              { key: 'state', label: 'State' },
            ]}
            rows={queue.data.items.map((b) => ({
              id: b.id,
              on: b.batch_on,
              count: String(b.payout_count),
              amount: <Money paise={b.total_amount_paise} showPaise />,
              maker:
                b.created_by_admin_id === adminId ? 'you — ask a colleague' : `admin #${b.created_by_admin_id}`,
              state: <StatusChip tone={batchTone(b.status)} label={b.status.replace(/_/g, ' ')} />,
            }))}
            footer={`${queue.data.total} waiting`}
          />
        </Section>
      )}
      <TaxQueue />
      <PunyaQueue />
    </>
  );
}

const TAX_ACTION_LABEL: Record<TaxApprovalRow['action'], string> = {
  rate_version: 'Rate version',
  rate_confirm: 'Rate sign-off',
  tds_challan: 'TDS deposit',
  gst_remittance: 'GST remittance',
  price_gst: 'Price sheet GST',
};

/** What the requester asked for, read from the body they sent — the approver agrees to this. */
export function taxWhat(row: TaxApprovalRow): ReactNode {
  const body = (row.payload.body ?? {}) as Record<string, string | number>;
  if (row.action === 'rate_version') {
    return `${String(body.rate_type)} at ${String(body.rate)} from ${String(body.effective_from)}`;
  }
  if (row.action === 'rate_confirm') {
    return `${TAX_ACTION_LABEL.rate_confirm} · rate #${String(row.payload.rate_version_id)} · ${String(body.source_note)}`;
  }
  if (row.action === 'price_gst') {
    // The exact lines whose GST fields the draft changes (doc 16 §8): the approver agrees to these.
    const changes = (row.payload.changes ?? []) as { label: string; is_taxable: boolean; hsn_sac_code: string | null }[];
    const lines = changes.map((c) => `${c.label}: ${c.is_taxable ? 'taxable' : 'not taxable'}${c.hsn_sac_code ? ` (${c.hsn_sac_code})` : ''}`);
    return `${TAX_ACTION_LABEL.price_gst} · sheet #${String(row.payload.version_id)} · ${lines.join('; ')}`;
  }
  if (row.action === 'tds_challan') {
    return (
      <>
        {String(body.challan_number)} · <Money paise={body.total_paise as number} showPaise />
      </>
    );
  }
  return (
    <>
      {String(body.return_period)}
      {row.payload.tcs === true ? ' (TCS)' : ''} · <Money paise={body.amount_paise as number} showPaise />
    </>
  );
}

/** The parked tax writes. Approving runs the write; the requester may only withdraw their own. */
function TaxQueue() {
  const { scope, adminId, toast } = useConsole();
  const pending = useTaxApprovals('pending');
  const decide = useDecideTaxApproval();
  const [confirm, setConfirm] = useState<{ row: TaxApprovalRow; decision: 'approve' | 'reject' } | null>(
    null,
  );
  /* The server records a requester's own refusal as a reject; to them it is a withdrawal. */
  const withdrawing = confirm?.row.requested_by_admin_id === adminId;

  if (pending.isPending) return <LoadingState message="Reading the tax queue…" />;
  if (pending.isError) {
    return <ErrorState title="Could not read the tax queue" cause={errorMessage(pending.error)} />;
  }
  if (pending.data.items.length === 0) {
    return (
      <Section
        title="Tax writes waiting for a checker"
        note="Nothing is written until a different finance admin approves"
      >
        <EmptyState
          inline
          title="No tax write is waiting for a second pair of eyes"
          body="A new rate version, a TDS deposit or a GST remittance lands here when somebody records it on the Tax screen."
        />
      </Section>
    );
  }
  return (
    <Section
      title="Tax writes waiting for a checker"
      note="Nothing is written until a different finance admin approves"
      pad={false}
    >
      <DataTable
        density="comfortable"
        columns={[
          { key: 'action', label: 'What' },
          { key: 'what', label: 'As asked' },
          { key: 'reason', label: 'Why' },
          { key: 'maker', label: 'Asked by' },
          { key: 'decide', label: '', align: 'right' },
        ]}
        rows={pending.data.items.map((row) => {
          const own = row.requested_by_admin_id === adminId;
          return {
            id: row.id,
            action: TAX_ACTION_LABEL[row.action],
            what: taxWhat(row),
            reason: row.reason,
            maker: own ? 'you — ask a colleague' : `admin #${row.requested_by_admin_id}`,
            decide: (
              <span style={{ display: 'inline-flex', gap: 8 }}>
                <ScopeAction
                  scope={scope}
                  need="finance"
                  size="sm"
                  variant="secondary"
                  onClick={() => setConfirm({ row, decision: 'reject' })}
                >
                  {own ? 'Withdraw' : 'Reject'}
                </ScopeAction>
                <span
                  data-tip={own ? 'Self-approval is refused by the server (403 self_approval_forbidden)' : undefined}
                  style={{ display: 'inline-flex' }}
                >
                  <ScopeAction
                    scope={scope}
                    need="finance"
                    size="sm"
                    disabled={own}
                    onClick={() => setConfirm({ row, decision: 'approve' })}
                  >
                    Approve
                  </ScopeAction>
                </span>
              </span>
            ),
          };
        })}
        footer={`${pending.data.total} waiting`}
      />
      {confirm !== null && (
        <ConfirmAction
          open
          title={
            confirm.decision === 'approve'
              ? `Approve this ${TAX_ACTION_LABEL[confirm.row.action].toLowerCase()}?`
              : withdrawing
                ? `Withdraw your ${TAX_ACTION_LABEL[confirm.row.action].toLowerCase()} request?`
                : `Reject this ${TAX_ACTION_LABEL[confirm.row.action].toLowerCase()}?`
          }
          confirmLabel={confirm.decision === 'approve' ? 'Approve and write' : withdrawing ? 'Withdraw it' : 'Reject'}
          phrase={confirm.decision === 'approve' ? 'APPROVE' : withdrawing ? 'WITHDRAW' : 'REJECT'}
          tone="error"
          reason={confirm.decision === 'approve' ? 'What you checked' : withdrawing ? 'Why you are withdrawing it' : 'Why not'}
          consequences={
            confirm.decision === 'approve'
              ? [
                  'The write runs now, against the ledger as it stands. Your name is recorded beside the person who asked.',
                ]
              : ['Nothing is written. The request stays on record as asked and refused.']
          }
          onCancel={() => setConfirm(null)}
          onConfirm={(note) =>
            decide.mutate(
              { id: confirm.row.id, decision: confirm.decision, note },
              {
                onSuccess: () => {
                  setConfirm(null);
                  toast({
                    tone: confirm.decision === 'approve' ? 'success' : 'info',
                    message:
                      confirm.decision === 'approve'
                        ? 'Approved and written'
                        : withdrawing
                          ? 'Withdrawn — nothing was written'
                          : 'Rejected — nothing was written',
                  });
                },
                onError: (error) => {
                  setConfirm(null);
                  toast({ tone: 'error', message: errorMessage(error) });
                },
              },
            )
          }
        />
      )}
    </Section>
  );
}

/** Manual Punya movements parked on the Punya tab. Approving writes the movement. */
function PunyaQueue() {
  const { scope, adminId, toast } = useConsole();
  const pending = usePunyaAdjustments('pending');
  const decide = useDecidePunyaAdjustment();
  const [confirm, setConfirm] = useState<{
    row: PunyaAdjustmentRow;
    decision: 'approve' | 'reject';
  } | null>(null);

  if (pending.isPending) return <LoadingState message="Reading the Punya queue…" />;
  if (pending.isError) {
    return <ErrorState title="Could not read the Punya queue" cause={errorMessage(pending.error)} />;
  }
  if (pending.data.items.length === 0) {
    return (
      <Section
        title="Punya adjustments waiting for a countersign"
        note="Four eyes at any size — the points move only when a different finance admin approves"
      >
        <EmptyState
          inline
          title="No Punya adjustment is waiting for a countersign"
          body="A manual credit or debit lands here when a finance admin submits it on the Punya tab. Nothing moves until somebody else approves it."
        />
      </Section>
    );
  }
  return (
    <Section
      title="Punya adjustments waiting for a countersign"
      note="Four eyes at any size — the points move only when a different finance admin approves"
      pad={false}
    >
      <DataTable
        density="comfortable"
        columns={[
          { key: 'devotee', label: 'Devotee' },
          { key: 'points', label: 'Points', align: 'right', mono: true },
          { key: 'memo', label: 'Memo' },
          { key: 'maker', label: 'Asked by' },
          { key: 'decide', label: '', align: 'right' },
        ]}
        rows={pending.data.items.map((row) => {
          const own = row.requested_by_admin_id === adminId;
          const tip = own ? 'Self-approval is refused by the server (403 self_approval_forbidden)' : undefined;
          return {
            id: row.id,
            devotee: `user #${row.user_id}`,
            points: row.credit ? `+${row.points}` : `-${row.points}`,
            memo: row.memo,
            maker: own ? 'you — ask a colleague' : `admin #${row.requested_by_admin_id}`,
            decide: (
              <span data-tip={tip} style={{ display: 'inline-flex', gap: 8 }}>
                <ScopeAction
                  scope={scope}
                  need="finance"
                  size="sm"
                  variant="secondary"
                  disabled={own}
                  onClick={() => setConfirm({ row, decision: 'reject' })}
                >
                  Reject
                </ScopeAction>
                <ScopeAction
                  scope={scope}
                  need="finance"
                  size="sm"
                  disabled={own}
                  onClick={() => setConfirm({ row, decision: 'approve' })}
                >
                  Approve
                </ScopeAction>
              </span>
            ),
          };
        })}
        footer={`${pending.data.items.length} waiting`}
      />
      {confirm !== null && (
        <ConfirmAction
          open
          title={confirm.decision === 'approve' ? 'Countersign this Punya adjustment?' : 'Reject this Punya adjustment?'}
          confirmLabel={confirm.decision === 'approve' ? 'Approve and move' : 'Reject'}
          phrase={confirm.decision === 'approve' ? 'APPROVE' : 'REJECT'}
          tone="error"
          reason={confirm.decision === 'reject' ? 'Why not' : undefined}
          consequences={
            confirm.decision === 'approve'
              ? [
                  'The points move now and change the platform liability. Your name is recorded beside the person who asked.',
                ]
              : ['Nothing moves. The request stays on record as asked and refused.']
          }
          onCancel={() => setConfirm(null)}
          onConfirm={(note) =>
            decide.mutate(
              { id: confirm.row.id, decision: confirm.decision, reason: note },
              {
                onSuccess: () => {
                  setConfirm(null);
                  toast({
                    tone: confirm.decision === 'approve' ? 'success' : 'info',
                    message: confirm.decision === 'approve' ? 'Countersigned — the points moved' : 'Rejected — nothing moved',
                  });
                },
                onError: (error) => {
                  setConfirm(null);
                  toast({ tone: 'error', message: errorMessage(error) });
                },
              },
            )
          }
        />
      )}
    </Section>
  );
}
